import React, { useState, useEffect } from "react";
import { getSMSTemplates, searchCustomerWithPhone, getJobTypes } from "../../service/ApiServices";
import Select from "react-select";
import Swal from "sweetalert2";
import axios from "axios";

const SMS = () => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [placeholderValues, setPlaceholderValues] = useState({});
  const [previewMessages, setPreviewMessages] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [jobTypes, setJobTypes] = useState([]);
  const [selectedJobTypes, setSelectedJobTypes] = useState({});
  const [customerLoading, setCustomerLoading] = useState(false);
  const [placeholderInstances, setPlaceholderInstances] = useState([]);
  const [loading2, setLoading2] = useState(false);

  useEffect(() => {
    fetchTemplates();
    fetchJobTypes();
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      setCustomerLoading(true);
      const response = await searchCustomerWithPhone();
      
      if (response.data) {
        const customerOptions = response.data.map((customer) => ({
          value: customer._id,
          label: customer.customerName,
          ...customer
        }));
        
        setCustomers(customerOptions);
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch customers'
      });
    } finally {
      setCustomerLoading(false);
    }
  };

  const fetchJobTypes = async () => {
    try {
      setLoading(true);
      const response = await getJobTypes();
      
      if (response) {
        const jobTypeOptions = response.map((jobType) => ({
          value: jobType.value,
          label: jobType.label,
        }));
        setJobTypes(jobTypeOptions);
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch job types'
      });
    } finally {
      setLoading(false);
    }
  };
 
  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await getSMSTemplates();
      if (response.data && response.data.SMSTemplates) {
        const templateOptions = response.data.SMSTemplates.map((template) => ({
          value: template._id,
          label: template.templateName,
          description: template.templateDescription,
          placeholders: template.placeholders
        }));
        setTemplates(templateOptions);
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch templates'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateChange = (selected) => {
    setSelectedTemplate(selected);
    setPlaceholderValues({});
    setSelectedCustomers([]);
    setSelectedJobTypes({});
    setPreviewMessages([]);
    
    if (selected?.description) {
      const instances = findPlaceholderInstances(selected.description);
      setPlaceholderInstances(instances);
      
      // Check if the template has a customer name placeholder
      // Check if the template has a customer name placeholder
      // (We don't need to store this anymore as we always render the customer selector)
    } else {
      setPlaceholderInstances([]);
    }
  };

  //extract placeholders from template
  const findPlaceholderInstances = (template) => {
    const regex = /{{([^}]+)}}/g;
    const instances = [];
    let match;
    
    while ((match = regex.exec(template)) !== null) {
      instances.push({
        placeholder: match[1].trim(),
        position: match.index,
        id: `${match[1].trim()}-${instances.length}`,
        fullMatch: match[0]
      });
    }
    
    return instances;
  };

  const handleCustomersSelect = (selected) => {
    setSelectedCustomers(selected || []);
    updateAllPreviews(selected || []);
  };

  const handleJobTypeSelect = (selected, instanceId) => {
    const updatedJobTypes = {
      ...selectedJobTypes,
      [instanceId]: selected
    };
    
    setSelectedJobTypes(updatedJobTypes);
    updateAllPreviews(selectedCustomers, null, updatedJobTypes);
  };

  const handlePlaceholderChange = (instanceId, value) => {
    const newValues = {
      ...placeholderValues,
      [instanceId]: value
    };
    
    setPlaceholderValues(newValues);
    updateAllPreviews(selectedCustomers, newValues);
  };

  const updateAllPreviews = (customers, updatedPlaceholderValues = null, updatedJobTypes = null) => {
    if (!selectedTemplate?.description || !customers.length) {
      setPreviewMessages([]);
      return;
    }

    // Use the provided updated values or fall back to the current state
    const currentPlaceholderValues = updatedPlaceholderValues || placeholderValues;
    const currentJobTypes = updatedJobTypes || selectedJobTypes;

    const previews = customers.map(customer => {
      return {
        customer: customer,
        message: generatePreviewForCustomer(customer, currentPlaceholderValues, currentJobTypes)
      };
    });

    setPreviewMessages(previews);
  };

  const generatePreviewForCustomer = (customer, currentPlaceholderValues = null, currentJobTypes = null) => {
    // Use the provided values or fall back to the current state
    const placeholders = currentPlaceholderValues || placeholderValues;
    const jobTypes = currentJobTypes || selectedJobTypes;
    
    // Create a deep copy of the template to avoid modifying the original
    let preview = selectedTemplate.description;
    
    // Sort instances by position in descending order to replace from end to start
    // This prevents position shifts when replacing text
    const sortedInstances = [...placeholderInstances].sort((a, b) => b.position - a.position);
    
    // Get positions after sorted (they'll be changed as we replace)
    const positionsMap = {};
    sortedInstances.forEach(instance => {
      positionsMap[instance.id] = instance.position;
    });
    
    // Process all instances
    sortedInstances.forEach(instance => {
      let value;
      const currentPos = positionsMap[instance.id];
      
      // For customer name placeholder, use the current customer's name
      if (instance.placeholder === 'CUSTOMER NAME' || instance.placeholder.toUpperCase() === 'CUSTOMER_NAME') {
        value = customer.customerName;
      } else {
        // For other placeholders, use the value from placeholderValues or selectedJobTypes
        if (instance.placeholder === 'ITEM TYPE' || instance.placeholder.toUpperCase() === 'ITEM_TYPE') {
          const jobType = jobTypes[instance.id];
          value = jobType ? jobType.label : null;
        } else {
          value = placeholders[instance.id];
        }
      }
      
      const replacement = value ? ` ${value}` : instance.fullMatch;
      
      const beforeReplacement = preview.substring(0, currentPos);
      const afterReplacement = preview.substring(currentPos + instance.fullMatch.length);
      
      preview = beforeReplacement + replacement + afterReplacement;
    });
    
    return preview;
  };

  // This function is separated from the placeholder input renderer
  const renderCustomerSelect = () => {
    return (
      <div className="mb-3">
        <label className="form-label">Select Customers</label>
        <Select
          options={customers}
          value={selectedCustomers}
          onChange={handleCustomersSelect}
          isLoading={customerLoading}
          placeholder="Select customers..."
          isClearable
          isMulti
          noOptionsMessage={() => "No customers available"}
          className="basic-multi-select"
          classNamePrefix="select"
        />
      </div>
    );
  };

  const renderPlaceholderInput = (instance) => {
    const { placeholder, id } = instance;
    const normalizedPlaceholder = placeholder.replace(/\s+/g, '_').toUpperCase();

    // Don't render customer selection here, it's handled separately
    if (normalizedPlaceholder === 'CUSTOMER_NAME' || placeholder === 'CUSTOMER NAME') {
      return null;
    }
    
    if (normalizedPlaceholder === 'ITEM_TYPE' || placeholder === 'ITEM TYPE') {
      return (
        <div key={id} className="mb-3">
          <label className="form-label">Select Item Type </label>
          <Select
            options={jobTypes}
            value={selectedJobTypes[id] || null}
            onChange={(selected) => handleJobTypeSelect(selected, id)}
            placeholder="Select item type..."
            isClearable
            isLoading={loading}
            noOptionsMessage={() => "No job types available"}
            className="basic-single"
            classNamePrefix="select"
          />
        </div>
      );
    }

    // For DATE placeholder
    if (normalizedPlaceholder === 'DATE' || placeholder === 'Select Date') {
      return (
        <div key={id} className="mb-3">
          <label className="form-label">Select Date</label>
          <input
            type="date"
            className="form-control"
            value={placeholderValues[id] || ''}
            onChange={(e) => handlePlaceholderChange(id, e.target.value)}
          />
        </div>
      );
    }

    // Default text input for other placeholders
    return (
      <div key={id} className="mb-3">
        <label className="form-label">{placeholder}</label>
        <input
          type="text"
          className="form-control"
          value={placeholderValues[id] || ''}
          onChange={(e) => handlePlaceholderChange(id, e.target.value)}
          placeholder={`Enter ${placeholder.toLowerCase()}`}
        />
      </div>
    );
  };

  const SendSMSToclient = async () => {
    setLoading2(true)
    try {
      // Create the formatted messages for API
      const messagesToSend = previewMessages.map(preview => ({
        customer: preview.customer,
        message: preview.message
      }));
      
      await axios.post('/sendsms', messagesToSend);
      
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'SMS sent successfully!',
        timer: 2000,
        showConfirmButton: false
      });
      setSelectedTemplate(null);
      setPreviewMessages([]);
  
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || error.message || 'Failed to send SMS',
      });
  
      console.error('SMS sending error:', error);
    } finally {
      setLoading2(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Send SMS</h4>
              <h6>Send Personalized SMS Messages to your client</h6>
            </div>
          </div>
        </div>

        <div className="row">
          {/* Left Side - Message Details */}
          <div className="col-md-6">
            <div className="card">
              <div className="card-body">
                <div className="mb-4">
                  <label className="form-label">Select Template</label>
                  <Select
                    options={templates}
                    value={selectedTemplate}
                    onChange={handleTemplateChange}
                    placeholder="Choose a template..."
                    isLoading={loading}
                    className="mb-3"
                  />
                </div>

                {selectedTemplate && (
                  <div className="placeholder-inputs">
                    <h6 className="mb-3">Fill Placeholder Values</h6>
                    
                    {/* Always render the customer select input, regardless of template */}
                    {renderCustomerSelect()}
                    
                    {/* Render other placeholder inputs */}
                    {placeholderInstances.map((instance) => (
                      <React.Fragment key={instance.id}>
                        {renderPlaceholderInput(instance)}
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Side - Message Preview */}
          <div className="col-md-6">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title mb-4">Message Previews</h5>
                {previewMessages.length > 0 ? (
                  <div>
                    {previewMessages.map((preview, index) => (
                      <div key={index} className="message-preview p-3 border rounded mb-3">
                        <h6 className="mb-2">For: {preview.customer.customerName}</h6>
                        <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                          {preview.message}
                        </pre>
                      </div>
                    ))}
                    <div className="mt-4">
                      <button 
                        className="btn btn-primary"
                        disabled={previewMessages.length === 0 || loading2}
                        onClick={() => SendSMSToclient()}
                      >
                        {loading2 ? 'Sending...' : `Send ${previewMessages.length} SMS${previewMessages.length > 1 ? 'es' : ''}`}
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-muted mb-0">
                    Select a template, fill in the placeholders, and select one or more customers to see the previews
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SMS;